import React, { useEffect, useState } from 'react';
import '../stylings/BucksPrivacyPolicy.css';

function BucksPrivacyPolicy() {
  const [isVisible, setIsVisible] = useState(false);
  const currentYear = new Date().getFullYear();
  
  // Animation on scroll effect
  useEffect(() => {
    setIsVisible(true);
    
    // Smooth scroll to section if hash is present in URL
    const hash = window.location.hash;
    if (hash) {
      const element = document.getElementById(hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
    
    // Scroll to top on component mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bucks-privacy-policy">
      {/* Hero Section */}
      <section className={`privacy-hero ${isVisible ? 'visible' : ''}`}>
        <div className="privacy-hero-content">
          <h1>Privacy Policy</h1>
          <p className="privacy-subtitle">
            At Buck's Farmstand, we value your privacy and are committed to protecting your personal information.
          </p>
          <div className="privacy-decoration"></div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="privacy-toc-section">
        <div className="privacy-container">
          <div className="privacy-toc">
            <h2>Contents</h2>
            <ul>
              <li><a href="#overview">Overview</a></li>
              <li><a href="#information-collection">Information We Collect</a></li>
              <li><a href="#information-use">How We Use Your Information</a></li>
              <li><a href="#information-sharing">Information Sharing</a></li>
              <li><a href="#data-security">Data Security</a></li>
              <li><a href="#data-retention">Data Retention</a></li>
              <li><a href="#your-rights">Your Rights</a></li>
              <li><a href="#third-party">Third-Party Services</a></li>
              <li><a href="#children">Children's Privacy</a></li>
              <li><a href="#changes">Changes To This Policy</a></li>
              <li><a href="#contact">Contact Us</a></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Policy Content */}
      <section className="privacy-content-section">
        <div className="privacy-container">
          
          {/* Overview */}
          <div id="overview" className="privacy-section">
            <h2>Overview</h2>
            <p>
              This Privacy Policy explains how Buck's Farmstand ("we," "us," or "our") collects, uses, and protects your personal information when you use our website, make purchases, or interact with our services. This policy was last updated on May 6, 2025.
            </p>
            <p>
              By using our website or services, you consent to the data practices described in this policy. We encourage you to read this policy carefully to understand our practices regarding your personal information.
            </p>
          </div>
          
          {/* Information Collection */}
          <div id="information-collection" className="privacy-section">
            <h2>Information We Collect</h2>
            <p>
              We collect several types of information from and about users of our website and services:
            </p>
            <ul className="privacy-list">
              <li>
                <span className="privacy-list-title">Personal Identifiers:</span> 
                <span className="privacy-list-content">Name, email address, phone number, and shipping/billing address (for delivery orders).</span>
              </li>
              <li>
                <span className="privacy-list-title">Order Information:</span>
                <span className="privacy-list-content">Products purchased, order history, delivery preferences, and related transaction details.</span>
              </li>
              <li>
                <span className="privacy-list-title">Usage Information:</span>
                <span className="privacy-list-content">Information about how you use our website, including browsing patterns, feature usage, and pages visited.</span>
              </li>
              <li>
                <span className="privacy-list-title">Device Information:</span>
                <span className="privacy-list-content">IP address, browser type, operating system, device information, and other technical data.</span>
              </li>
            </ul>
            <div className="privacy-notice">
              <strong>Important:</strong> We do not collect or store credit card information. All payment processing is handled securely by Square, our third-party payment processor.
            </div>
          </div>
          
          {/* Information Use */}
          <div id="information-use" className="privacy-section">
            <h2>How We Use Your Information</h2>
            <p>
              We use the information we collect for various purposes, including:
            </p>
            <ul className="privacy-list">
              <li>
                <span className="privacy-list-title">Order Processing:</span> 
                <span className="privacy-list-content">To process and fulfill your orders, deliver products, and provide customer support.</span>
              </li>
              <li>
                <span className="privacy-list-title">Communication:</span>
                <span className="privacy-list-content">To communicate with you about your orders, provide updates, respond to inquiries, and send transactional messages.</span>
              </li>
              <li>
                <span className="privacy-list-title">Service Improvement:</span>
                <span className="privacy-list-content">To improve our website, products, and services based on usage patterns and customer feedback.</span>
              </li>
              <li>
                <span className="privacy-list-title">Marketing:</span>
                <span className="privacy-list-content">To send marketing communications, special offers, and information about new products or services (with your consent).</span>
              </li>
              <li>
                <span className="privacy-list-title">Analytics:</span>
                <span className="privacy-list-content">To analyze usage patterns, track website performance, and optimize user experience.</span>
              </li>
              <li>
                <span className="privacy-list-title">Legal Compliance:</span>
                <span className="privacy-list-content">To comply with applicable laws, regulations, and legal obligations.</span>
              </li>
            </ul>
          </div>
          
          {/* Information Sharing */}
          <div id="information-sharing" className="privacy-section">
            <h2>Information Sharing</h2>
            <p>
              We do not sell your personal information to third parties. We may share your information in the following circumstances:
            </p>
            <ul className="privacy-list">
              <li>
                <span className="privacy-list-title">Service Providers:</span> 
                <span className="privacy-list-content">With trusted third-party service providers who help us operate our business and website, such as payment processors (Square).</span>
              </li>
              <li>
                <span className="privacy-list-title">Legal Requirements:</span>
                <span className="privacy-list-content">When required by law, court order, or government regulation, or to protect our rights, property, or safety.</span>
              </li>
            </ul>
          </div>
          
          {/* Data Security */}
          <div id="data-security" className="privacy-section">
            <h2>Data Security</h2>
            <p>
              We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
            <p>
              Our payment processing is handled by Square, which maintains industry-standard security protocols and is PCI-DSS compliant. We do not store or have access to your payment card details.
            </p>
          </div>
          
          {/* Data Retention */}
          <div id="data-retention" className="privacy-section">
            <h2>Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, including satisfying legal or reporting requirements.
            </p>
            <p>
              For delivery orders, we delete specific address details (house number and street) after a certain period, while retaining zip code and city information for delivery metrics and business analytics purposes.
            </p>
          </div>
          
          {/* Your Rights */}
          <div id="your-rights" className="privacy-section">
            <h2>Your Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="privacy-list">
              <li>
                <span className="privacy-list-title">Access:</span> 
                <span className="privacy-list-content">Request access to your personal information.</span>
              </li>
              <li>
                <span className="privacy-list-title">Correction:</span>
                <span className="privacy-list-content">Request correction of inaccurate personal information.</span>
              </li>
              <li>
                <span className="privacy-list-title">Deletion:</span>
                <span className="privacy-list-content">Request deletion of your personal information under certain circumstances.</span>
              </li>
              <li>
                <span className="privacy-list-title">Opt-out:</span>
                <span className="privacy-list-content">Opt out of marketing communications or certain types of data processing.</span>
              </li>
            </ul>
            <p>
              To exercise any of these rights, please contact us using the information provided in the "Contact Us" section.
            </p>
          </div>
          
          {/* Third-Party Services */}
          <div id="third-party" className="privacy-section">
            <h2>Third-Party Services</h2>
            <p>
              Our website integrates with third-party services that may collect information about you independently. Our primary third-party partner is Square, which handles our payment processing.
            </p>
            <p>
              When you make a purchase, your payment information is provided directly to Square and is subject to their privacy policy and terms of service. We recommend reviewing Square's privacy policy to understand how they process your payment information.
            </p>
          </div>
          
          {/* Children's Privacy */}
          <div id="children" className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>
              Our website and services are not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.
            </p>
          </div>
          
          {/* Changes to Policy */}
          <div id="changes" className="privacy-section">
            <h2>Changes To This Policy</h2>
            <p>
              We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will post the updated policy on our website with a revised effective date. We encourage you to review this policy regularly.
            </p>
          </div>
          
          {/* Contact Us */}
          <div id="contact" className="privacy-section">
            <h2>Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <div className="privacy-contact-info">
              <p><strong>Buck's Farmstand</strong></p>
              <p>4250 W 123rd St</p>
              <p>Alsip, IL 60803</p>
              <p>Email: privacy@bucksfarmstand.com</p>
              <p>Phone: (708) 555-1234</p>
            </div>
          </div>
          
          <div className="privacy-footer">
            <p>&copy; {currentYear} Buck's Farmstand. All Rights Reserved.</p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BucksPrivacyPolicy;